import { useMemo } from "react";
import classNames from "classnames";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/TooltipV3";
import { getBudgetBandColor } from "../budgetCategoryPalette";
import { buildBudgetBreakdown } from "./BudgetBreakdown.utils";
import { BudgetBreakdownProps } from "./BudgetBreakdown.types";

const formatAmount = (currencySymbol: string, value: number): string =>
  `${currencySymbol}${Math.round(value).toLocaleString()}`;

export const BudgetBreakdown: React.FC<BudgetBreakdownProps> = ({
  categories,
  contingencyPercentage,
  currencySymbol,
}) => {
  const { segments, grandTotal } = useMemo(
    () => buildBudgetBreakdown(categories, contingencyPercentage),
    [categories, contingencyPercentage]
  );

  const visibleSegments = useMemo(
    () => segments.filter((segment) => segment.sharePercent > 0),
    [segments]
  );

  if (visibleSegments.length === 0 || grandTotal <= 0) return null;

  return (
    <div className="mb-4" role="img" aria-label="Budget category breakdown">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
        {visibleSegments.map((segment) => (
          <Tooltip key={segment.id}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={classNames(
                  "h-full shrink-0 border-0 p-0 transition-opacity hover:opacity-90",
                  "cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                  "first:rounded-l-full last:rounded-r-full"
                )}
                style={{
                  width: `${segment.sharePercent}%`,
                  minWidth: "6px",
                  backgroundColor: getBudgetBandColor(segment.colorIndex),
                }}
                aria-label={`${segment.name}: ${formatAmount(currencySymbol, segment.estimate)} (${segment.sharePercent.toFixed(0)}%)`}
              />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={8} className="px-3 py-2">
              <div className="flex flex-col gap-0.5">
                <span className="font-medium capitalize">{segment.name}</span>
                <span className="tabular-nums text-foreground/80">
                  {formatAmount(currencySymbol, segment.estimate)} ·{" "}
                  {segment.sharePercent.toFixed(0)}%
                </span>
              </div>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};
